import AIState, { useAIState } from "@/components/AIState";
import EmailState, { useEmailState } from "@/components/EmailState";
import { submitEmail, refreshEmail } from "@/lib/emailOperations";
import OperationalState, { operationallyAllowed } from "@/components/OperationalState";
import EmptyState from "@/components/EmptyState";
import { useRead, ReadState, isCollection, isVideos } from "@/components/LoadingStates";
import { useState, useRef, useCallback, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import { useLocation } from "react-router-dom";
import SectionTabs, { settingsTabsForLocation } from "@/components/SectionTabs";
import api, { formatError, API } from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Bell, Loader2, Mail, Zap, Eye, Clock, Send, Radar } from "lucide-react";
import { formatNumber, timeAgo, classNames } from "@/lib/format";
import DigestPreview from "@/components/DigestPreview";

const WINDOWS = [
  { v: 24, l: "Last 24 hours" },
  { v: 72, l: "Last 3 days" },
  { v: 168, l: "Last 7 days" },
];

export default function Alerts() {
  const location = useLocation();
  return (
    <div className="space-y-6">
      <SectionTabs tabs={settingsTabsForLocation(location)} activeKey="notifications" ariaLabel="Settings sections" data-testid="section-tabs-settings" />
      <AlertsContent />
    </div>
  );
}

export function AlertsContent({ embedded = false }) {
  const ai = useAIState("weekly_brief");
  const email = useEmailState();
  const { canEdit, user } = useAuth();
  const scope = `${user?.id}|${user?.workspace_id || "personal"}`;
  const scopeRef = useRef(scope); scopeRef.current = scope;
  const [emailOperationState, setEmailOperationState] = useState(null);
  const emailOperations = emailOperationState?.scope === scope ? emailOperationState.data : {};
  const refreshEmailOperations = useCallback(async () => {
    const data = {};
    for (const purpose of ["digest", "weekly_brief"]) {
      try { data[purpose] = await refreshEmail(user, purpose); } catch (_) { /* retain unresolved identity */ }
    }
    if (scopeRef.current === scope) setEmailOperationState({ scope, data });
  }, [scope, user]);
  useEffect(() => { refreshEmailOperations(); }, [refreshEmailOperations]);
  const settingsRead = useRead("/alerts/settings", (d) => d && typeof d.enabled === "boolean" && typeof d.window_hours === "number" && typeof d.min_multiplier === "number");
  const settings = settingsRead.data;
  const emailEnabled = operationallyAllowed(settings?.operations, "email_delivery");
  const dailyEnabled = (emailEnabled && operationallyAllowed(settings?.operations, "daily_digest") && email.allowed("digest")) || emailOperations.digest?.state === "persistence_pending";
  const weeklyEnabled = (emailEnabled && operationallyAllowed(settings?.operations, "weekly_brief") && email.allowed("weekly_brief")) || emailOperations.weekly_brief?.state === "persistence_pending";
  const setSettings = settingsRead.setData;
  const watchlistsRead = useRead("/watchlists", isCollection);
  const watchlists = watchlistsRead.data || [];
  const digestRead = useRead(settings ? `/alerts/digest?window_hours=${settings.window_hours}` : null, (d) => d && typeof d.total_new === "number" && Array.isArray(d.creators) && d.creators.every((c) => c && isVideos(c.videos)));
  const digest = digestRead.data;
  const historyRead = useRead("/alerts/history", isCollection);
  const history = historyRead.data || [];
  const [saving, setSaving] = useState(false);
  const [running, setRunning] = useState(false);
  const [runningWeekly, setRunningWeekly] = useState(false);
  const [view, setView] = useState("app");
  const previewRead = useRead(view === "email" && settings ? `/alerts/digest/preview?window_hours=${settings.window_hours}` : null, (d) => typeof d === "string");
  const html = previewRead.data || "";
  const loadDigest = () => digestRead.reload().catch(() => {});
  const loadHistory = () => historyRead.reload().catch(() => {});

  const update = (patch) => { if (canEdit) setSettings((s) => ({ ...s, ...patch })); };

  const save = async () => {
    if (!canEdit || saving || !settings) return;
    setSaving(true);
    try {
      const { enabled, watchlist_id, window_hours, min_multiplier, weekly_brief_enabled } = settings;
      const { data } = await api.put("/alerts/settings", { enabled, watchlist_id: watchlist_id || null, window_hours, min_multiplier, weekly_brief_enabled: weekly_brief_enabled !== false });
      setSettings(data);
      loadDigest(data);
      toast.success("Alert settings saved");
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setSaving(false);
    }
  };

  const runNow = async () => {
    if (!canEdit || !dailyEnabled || running) return;
    setRunning(true);
    try {
      const data = await submitEmail(user, "digest", "/alerts/run-now", { watchlist_id: settings.watchlist_id, window_hours: settings.window_hours, min_multiplier: settings.min_multiplier }, { standard: email.standard });
      const d = data.delivery;
      if (d.status === "sent") toast.success(`Digest accepté par le fournisseur pour ${d.to} — ${data.digest.total_new} new breakouts ; réception en boîte non confirmée.`);
      else if (d.status === "accepted") toast.success("Email accepté par le fournisseur ; réception en boîte non confirmée.");
      else if (d.status === "disabled") toast.info("Livraison désactivée par la politique privée ; aucun e-mail envoyé.");
      else if (d.status === "failed") toast.error(`Digest saved but email failed: ${d.error}`);
      else toast.success(`Digest generated — ${data.digest.total_new} new breakouts (fournisseur e-mail non configuré)`);
      loadHistory();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRunning(false);
      email.read.reload().catch(() => {}); refreshEmailOperations();
    }
  };

  const runWeekly = async () => {
    if (!canEdit || !weeklyEnabled || runningWeekly) return;
    setRunningWeekly(true);
    try {
      const data = await submitEmail(user, "weekly_brief", "/alerts/weekly-brief/run-now", {}, { standard: email.standard });
      const d = data.delivery;
      if (d.status === "sent") toast.success(`Brief hebdomadaire accepté par le fournisseur pour ${d.to} ; réception en boîte non confirmée.`);
      else if (d.status === "accepted") toast.success("Email accepté par le fournisseur ; réception en boîte non confirmée.");
      else if (d.status === "disabled") toast.info("Livraison désactivée par la politique privée ; aucun e-mail envoyé.");
      else if (d.status === "failed") toast.error(`Brief saved but email failed: ${d.error}`);
      else toast.success("Weekly brief generated (fournisseur e-mail non configuré)");
      loadHistory();
    } catch (e) {
      toast.error(formatError(e));
    } finally {
      setRunningWeekly(false);
      email.read.reload().catch(() => {}); refreshEmailOperations();
    }
  };


  const Title = embedded ? "h2" : "h1";

  return (
    <div className="space-y-6" data-testid="alerts-page">
      <div>
        <div className="flex items-center gap-2">
          <Bell className="h-6 w-6 text-primary" />
          <Title className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight">Outlier Alerts</Title>
        </div>
        <p className="text-muted-foreground text-sm mt-1">
          A daily digest of every new breakout from your watched creators — preview it here before email is wired up.
        </p>
      </div>
      {!canEdit && <p role="status" className="text-sm text-muted-foreground">Les notifications de ce workspace sont consultables en lecture seule.</p>}

      <AIState read={ai.read} scope="notifications" />
      <EmailState read={email.read} operations={emailOperations} onRefresh={refreshEmailOperations} />
      <OperationalState state={settings?.operations} capabilities={["daily_digest", "weekly_brief", "manual_ai", "email_delivery"]} scope="notifications" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ReadState read={settingsRead} scope="alerts-settings">
        {settings && <Card className="p-5 space-y-5" data-testid="alert-settings-card">
          <div className="flex items-center justify-between">
            <div>
              <div className="font-semibold">Daily digest</div>
              <div className="text-xs text-muted-foreground">Generated every 24h by the scheduler</div>
            </div>
            {canEdit ? <Switch checked={settings.enabled} onCheckedChange={(v) => update({ enabled: v })} data-testid="alert-enabled-switch" /> : <span className="text-sm">{settings.enabled ? "Enabled" : "Disabled"}</span>}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Send to</label>
            <div className="relative">
              <Mail className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input type="email" value={settings.default_email || ""} readOnly disabled className="pl-8" data-testid="alert-email-input" />
            </div>
            <p className="text-[11px] text-muted-foreground" data-testid="alert-email-note">Alerts are always sent to your account email.</p>
            <div className={`inline-flex items-center gap-1.5 text-[11px] ${settings.provider_connected ? "text-[hsl(var(--emerald))]" : "text-[hsl(var(--amber))]"}`} data-testid="alert-provider-status">
              <span className={`h-1.5 w-1.5 rounded-full ${settings.provider_connected ? "bg-[hsl(var(--emerald))]" : "bg-[hsl(var(--amber))]"}`} />
              {settings.provider_connected ? `Clé Resend présente côté serveur · expéditeur ${settings.sender} — validité et livraison non vérifiées` : email.standard
                ? "Email provider not configured — digests are saved in-app only"
                : "Fournisseur email non configuré : aucun nouvel envoi ni nouveau digest. L’aperçu et les résultats déjà générés restent disponibles."}
            </div>
            {settings.provider_connected && (
              <p className="text-[11px] text-muted-foreground" data-testid="alert-provider-hint">Until you verify a domain in Resend, it only delivers to the email on your Resend account.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Scope</label>
            <ReadState read={watchlistsRead} scope="alerts-watchlists">{canEdit ? <Select value={settings.watchlist_id || "all"} onValueChange={(v) => update({ watchlist_id: v === "all" ? null : v })}>
              <SelectTrigger data-testid="alert-watchlist-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All tracked creators</SelectItem>
                {watchlists.map((w) => <SelectItem key={w.id} value={w.id}>{w.name}</SelectItem>)}
              </SelectContent>
            </Select> : <p className="text-sm">{settings.watchlist_id ? watchlists.find((watchlist) => watchlist.id === settings.watchlist_id)?.name || "Selected watchlist" : "All tracked creators"}</p>}</ReadState>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Window</label>
              {canEdit ? <Select value={String(settings.window_hours)} onValueChange={(v) => update({ window_hours: Number(v) })}>
                <SelectTrigger data-testid="alert-window-select"><SelectValue /></SelectTrigger>
                <SelectContent>{WINDOWS.map((w) => <SelectItem key={w.v} value={String(w.v)}>{w.l}</SelectItem>)}</SelectContent>
              </Select> : <p className="text-sm">{WINDOWS.find((window) => window.v === settings.window_hours)?.l || `${settings.window_hours} hours`}</p>}
            </div>
            <div className="space-y-1.5">
              <label className="text-xs uppercase tracking-wider text-muted-foreground font-medium">Min lift</label>
              {canEdit ? <Input type="number" step="0.5" min="1" value={settings.min_multiplier} onChange={(e) => update({ min_multiplier: Number(e.target.value) })} data-testid="alert-min-multiplier-input" /> : <p className="text-sm">{settings.min_multiplier}x</p>}
            </div>
          </div>

          <div className="flex items-center justify-between pt-1">
            <div>
              <div className="font-semibold text-sm">Weekly Trend Radar brief</div>
              <div className="text-xs text-muted-foreground">Every Monday 08:00 UTC, same address</div>
            </div>
            {canEdit ? <Switch checked={settings.weekly_brief_enabled !== false} onCheckedChange={(v) => update({ weekly_brief_enabled: v })} data-testid="alert-weekly-switch" /> : <span className="text-sm">{settings.weekly_brief_enabled !== false ? "Enabled" : "Disabled"}</span>}
          </div>

          {canEdit && <div className="flex gap-2 pt-1">
            <Button onClick={save} disabled={saving} className="flex-1" data-testid="alert-save-button">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null} Save settings
            </Button>
            <Button variant="outline" onClick={runNow} disabled={running || !dailyEnabled} title="Send daily digest now" data-testid="alert-run-now-button">
              {running ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            </Button>
            <Button variant="outline" onClick={runWeekly} disabled={runningWeekly || !weeklyEnabled} title="Send weekly brief now" data-testid="alert-weekly-run-now-button">
              {runningWeekly ? <Loader2 className="h-4 w-4 animate-spin" /> : <Radar className="h-4 w-4" />}
            </Button>
          </div>}
        </Card>}
        </ReadState>

        <Card className="p-5 lg:col-span-2" data-testid="alert-preview-card">
          <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
            <div>
              <h3 className="font-display font-bold text-lg">Digest preview</h3>
              <p className="text-xs text-muted-foreground">
                {digest ? `${digest.total_new} new breakouts · ${digest.creators_count} creators · ${formatNumber(digest.total_views)} views · last ${digest.window_hours}h` : "Loading…"}
              </p>
            </div>
            <div className="flex gap-1 rounded-lg bg-muted p-1">
              {[{ k: "app", l: "In-app" }, { k: "email", l: "Email" }].map((t) => (
                <button key={t.k} onClick={() => setView(t.k)} data-testid={`alert-view-${t.k}`} className={classNames("px-3 py-1.5 text-xs rounded-md transition-colors font-medium", view === t.k ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground")}>{t.l}</button>
              ))}
            </div>
          </div>
          {view === "app" ? (
            settings ? <ReadState read={digestRead} scope="alerts-digest">{digest && <DigestPreview digest={digest} />}</ReadState> : <EmptyState testid="alerts-digest-unavailable" title="Aperçu en attente des paramètres" description="Chargez les paramètres de notification pour accéder à cet aperçu." />
          ) : (
            <ReadState read={previewRead} scope="alerts-preview" empty={!html.trim()} title="Aucun aperçu disponible"><iframe title="Email preview" srcDoc={html} className="w-full h-[640px] rounded-lg border border-border bg-white" data-testid="alert-email-iframe" /></ReadState>
          )}
        </Card>
      </div>

      <Card className="p-5" data-testid="alert-history-card">
        <h3 className="font-display font-bold text-lg mb-1">Digest history</h3>
        <p className="text-xs text-muted-foreground mb-4">Every digest the scheduler (or you) generated.</p>
        <ReadState read={historyRead} scope="alert-history" empty={!history.length} title="Aucun digest dans l’historique" description="Les digests générés apparaîtront ici." >
          <div className="divide-y divide-border">
            {history.map((h) => (
              <div key={h.id} className="flex items-center gap-3 py-2.5" data-testid={`alert-history-row-${h.id}`}>
                <Clock className={`h-4 w-4 ${h.kind === "weekly_brief" ? "text-primary" : "text-muted-foreground"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium">
                    {h.kind === "weekly_brief" ? <>Weekly brief <span className="text-muted-foreground font-normal">· {h.digest.headline || `${h.digest.total_new} outliers`} · {h.trigger}</span></>
                      : <>{h.digest.total_new} new breakout{h.digest.total_new !== 1 ? "s" : ""} <span className="text-muted-foreground font-normal">· {h.digest.window_hours}h window · {h.trigger}</span></>}
                  </div>
                  <div className="text-xs text-muted-foreground">{timeAgo(h.created_at)} · to {h.delivery.to}{h.delivery.error ? ` · ${h.delivery.error}` : ""}</div>
                </div>
                <span className={classNames("text-[10px] font-bold uppercase tracking-wider rounded-full px-2 py-0.5", h.delivery.status === "sent" ? "bg-[hsl(var(--emerald)/0.12)] text-[hsl(var(--emerald))]" : h.delivery.status === "failed" ? "bg-[hsl(var(--rose)/0.12)] text-[hsl(var(--rose))]" : "bg-muted text-muted-foreground")} data-testid={`alert-history-status-${h.id}`}>{h.delivery.status.replace("_", " ")}</span>
                <a href={`${API}/alerts/history/${h.id}/html`} target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid={`alert-history-open-${h.id}`}><Eye className="h-3.5 w-3.5" /> View</a>
              </div>
            ))}
          </div>
        </ReadState>
      </Card>
    </div>
  );
}
